import { SecteurTemplate } from "./SecteurTemplate";

export function SecteurRHPage() {
  return (
    <SecteurTemplate
      metier="Ressources Humaines"
      slug="rh"
      title="Logiciel IA pour les Ressources Humaines | Raguia"
      description="Raguia est l'assistant IA souverain pour les RH. Trouvez instantanément les informations dans vos conventions collectives, fiches de paie et règlements intérieurs."
      heroTitle="Gagnez un temps précieux sur vos requêtes RH"
      heroSubtitle="Vos conventions collectives, fiches de poste et règlements intérieurs centralisés. Posez vos questions, l'IA répond avec la source exacte."
      painPoints={[
        "Perte de temps à chercher dans des centaines de pages de conventions collectives.",
        "Questions répétitives des collaborateurs sur les politiques de congés et avantages.",
        "Risque d'erreur en consultant une ancienne version d'un document RH.",
      ]}
      solutions={[
        "Interrogez vos documents RH en langage naturel.",
        "Obtenez la page exacte de la convention collective ou du règlement en 2 secondes.",
        "Mise à jour automatique grâce à l'agent local sur votre serveur."
      ]}
      useCases={[
        {
          title: "Analyse des Conventions",
          description: "Posez la question : 'Quelle est la prime de licenciement pour un cadre de 5 ans d'ancienneté ?'. Raguia extrait la réponse et cite la page de la convention."
        },
        {
          title: "Self-service Salariés",
          description: "Les managers peuvent interroger la base de connaissances pour répondre vite et sans erreur aux questions légales de leurs équipes."
        },
        {
          title: "Onboarding facilité",
          description: "Centralisez les livrets d'accueil et fiches de mutuelle pour que vos nouveaux arrivants trouvent leurs informations instantanément."
        }
      ]}
      faq={[
        { q: "Raguia peut-il lire nos conventions collectives et accords d'entreprise ?", a: "Oui, Raguia analyse parfaitement les documents complexes, longs et très structurés comme les conventions collectives, même en format PDF." },
        { q: "Comment garantissez-vous la confidentialité des données RH ?", a: "L'hébergement est assuré en France (OVH) et les données sont isolées. Raguia n'utilise jamais vos documents pour entraîner des modèles publics." },
        { q: "Les collaborateurs peuvent-ils utiliser Raguia directement ?", a: "Oui, vous pouvez leur donner un accès restreint au chatbot pour qu'ils posent leurs questions courantes (congés, mutuelle) en self-service." },
      ]}
    />
  );
}

export function SecteurRecrutementPage() {
  return (
    <SecteurTemplate
      metier="Cabinets de Recrutement"
      slug="recrutement"
      title="Assistant IA pour le Recrutement | Raguia"
      description="Analysez des centaines de CV, extractions de compétences et fiches de poste en quelques secondes avec Raguia, l'IA B2B sécurisée."
      heroTitle="Ne laissez plus aucun talent se perdre dans vos dossiers"
      heroSubtitle="Analysez et interrogez vos CVthèques et fiches de poste avec une IA qui respecte la confidentialité de vos candidats."
      painPoints={[
        "CVthèque massive mais impossible de chercher par contexte ou compétences précises.",
        "Temps passé à relire des CV pour vérifier une compétence spécifique.",
        "Besoin de respecter strictement le RGPD pour les données des candidats."
      ]}
      solutions={[
        "Posez des questions croisées : 'Qui a 5 ans d'XP en React et parle Allemand ?'.",
        "Raguia lit les CV (même scannés grâce à l'OCR) et vous sort les bons profils.",
        "Données 100% hébergées en France, aucun entraînement des modèles sur les CV."
      ]}
      useCases={[
        {
          title: "Sourcing express",
          description: "Recherchez des candidats dans votre propre vivier en posant des questions complexes à l'IA plutôt qu'en utilisant de simples mots-clés."
        },
        {
          title: "Synthèse de profil",
          description: "Demandez à l'IA de vous faire une synthèse des points forts d'un profil par rapport à une fiche de poste spécifique."
        },
        {
          title: "Matching intelligent",
          description: "Croisez instantanément les exigences d'un client avec l'historique complet de vos candidats placés et sourcés."
        }
      ]}
      faq={[
        { q: "L'OCR fonctionne-t-il sur des CV scannés ou sous forme d'image ?", a: "Oui, notre système OCR extrait le texte des PDF scannés, des images et des formats non standards fréquents dans le recrutement." },
        { q: "Raguia est-il conforme au RGPD concernant les données des candidats ?", a: "Absolument. Les données restent en France, l'IA ne s'entraîne pas dessus, et la suppression des données candidats est garantie sur simple demande." },
        { q: "Puis-je croiser les informations de plusieurs CV en même temps ?", a: "Oui, vous pouvez interroger toute votre base pour trouver, par exemple, tous les profils ayant une compétence spécifique mentionnée dans leurs expériences." },
      ]}
    />
  );
}

export function SecteurAvocatsPage() {
  return (
    <SecteurTemplate
      metier="Avocats & Juristes"
      slug="avocats"
      title="IA générative pour Avocats et Juristes | Raguia"
      description="Raguia aide les cabinets d'avocats à analyser la jurisprudence, les contrats et les conclusions tout en garantissant le secret professionnel."
      heroTitle="L'IA souveraine au service de votre cabinet d'avocats"
      heroSubtitle="Analysez vos conclusions, contrats et dossiers de plaidoirie en quelques secondes. Le tout avec une garantie de secret professionnel absolu."
      painPoints={[
        "Heures perdues à chercher une clause spécifique dans un contrat de 200 pages.",
        "Risque de confidentialité critique en utilisant des IA publiques comme ChatGPT.",
        "Difficulté à faire des synthèses rapides sur des dizaines de pièces d'un dossier complexe."
      ]}
      solutions={[
        "Architecture isolée (RAG) : vos documents ne nourrissent aucune IA publique.",
        "Retrouvez la source exacte d'un argumentaire dans vos anciennes conclusions.",
        "Hébergement OVH (France) pour garantir la souveraineté de vos données clients."
      ]}
      useCases={[
        {
          title: "Analyse de Contrats",
          description: "Demandez : 'Quelles sont les clauses de résiliation dans ce contrat ?'. L'IA vous sort la liste avec les références exactes des articles."
        },
        {
          title: "Préparation de Dossier",
          description: "Uploadez toutes les pièces d'un litige et demandez à l'IA de créer une chronologie des faits basée uniquement sur vos documents."
        },
        {
          title: "Recherche de Précédents",
          description: "Interrogez l'historique des conclusions de votre cabinet pour retrouver comment un confrère avait traité un point de droit similaire."
        }
      ]}
      faq={[
        { q: "Le secret professionnel est-il respecté ?", a: "Oui. L'architecture garantit que vos documents restent strictement confidentiels. Vos dossiers ne nourrissent aucune base de données publique." },
        { q: "Raguia peut-il analyser de la jurisprudence ou seulement nos propres documents ?", a: "Raguia se base uniquement sur les documents que vous lui fournissez (vos dossiers, vos conclusions, ou de la jurisprudence que vous importez)." },
        { q: "Gérez-vous le cloisonnement par dossier ou par équipe ?", a: "Oui, la gestion des droits permet d'isoler les documents selon les départements, les équipes, ou de façon étanche par dossier client au sein de votre cabinet." },
      ]}
    />
  );
}

export function SecteurImmobilierPage() {
  return (
    <SecteurTemplate
      metier="Professionnels de l'Immobilier"
      slug="immobilier"
      title="IA documentaire pour l'Immobilier | Raguia"
      description="Gérez vos baux, diagnostics techniques, DPE et mandats de gestion facilement avec Raguia. Accélérez vos transactions immobilières."
      heroTitle="Accélérez vos transactions immobilières"
      heroSubtitle="Diagnostics, baux, AG de copropriété : posez vos questions à vos dossiers immobiliers pour répondre plus vite à vos clients."
      painPoints={[
        "Dossiers de vente à rallonge avec des centaines de pages de diagnostics techniques.",
        "Questions de locataires sur des baux signés il y a des années.",
        "Perte d'informations dans les anciens comptes rendus d'assemblée générale."
      ]}
      solutions={[
        "Extrayez les informations d'un DPE ou d'un rapport amiante en 2 secondes.",
        "Chatbot interactif capable de répondre aux requêtes de syndic.",
        "Lecture des plans et documents scannés grâce à notre OCR intégré."
      ]}
      useCases={[
        {
          title: "Synthèse de Diagnostics",
          description: "Demandez à l'IA de lister toutes les anomalies électriques signalées dans un dossier de diagnostic technique de 300 pages."
        },
        {
          title: "Gestion Locative",
          description: "Retrouvez instantanément la clause d'indexation des loyers d'un bail commercial complexe."
        },
        {
          title: "Syndic de Copropriété",
          description: "Interrogez les 10 dernières années de PV d'Assemblée Générale pour savoir quand a été votée la réfection de la toiture."
        }
      ]}
      faq={[
        { q: "Raguia lit-il les plans et les diagnostics (DPE, amiante) ?", a: "Oui, notre technologie OCR avancée permet de lire le texte contenu dans les diagnostics techniques, même lorsqu'il s'agit de documents complexes ou scannés." },
        { q: "Pouvons-nous donner accès au chatbot à nos clients (propriétaires, locataires) ?", a: "Oui, avec la fonctionnalité marque blanche, vos clients peuvent disposer d'un portail à vos couleurs pour interroger leurs propres documents (baux, AG, etc.)." },
        { q: "Est-ce que l'IA peut extraire des données d'un bail pour remplir notre logiciel ?", a: "L'IA excelle pour retrouver des informations précises dans de longs baux (indexation, charges, conditions particulières) et vous faire gagner un temps précieux lors de la saisie ou de la vérification." },
      ]}
    />
  );
}

export function SecteurNotairesPage() {
  return (
    <SecteurTemplate
      metier="Notaires"
      slug="notaires"
      title="IA pour Études Notariales | Raguia"
      description="L'IA souveraine pour les notaires : analysez les actes de vente, successions et titres de propriété avec un niveau de sécurité maximal."
      heroTitle="Sécurisez et accélérez la rédaction de vos actes"
      heroSubtitle="L'IA souveraine conçue pour s'intégrer au flux de travail des études notariales. Retrouvez vos informations dans vos titres de propriété et successions."
      painPoints={[
        "Temps d'analyse considérable sur les titres de propriété antérieurs (origines de propriété).",
        "Volume de papier et de documents scannés complexes à exploiter.",
        "Nécessité absolue de secret professionnel et de conformité stricte."
      ]}
      solutions={[
        "Recherche intelligente dans les historiques de l'étude (actes, donations, successions).",
        "Extraction automatique des servitudes et clauses particulières des anciens actes scannés.",
        "Hébergement SecNumCloud-compatible (OVH) et isolation totale des données."
      ]}
      useCases={[
        {
          title: "Origine de Propriété",
          description: "Parcourez instantanément des décennies d'actes pour retracer la chaîne de propriété d'un bien immobilier."
        },
        {
          title: "Analyse de Servitudes",
          description: "Identifiez rapidement toutes les servitudes de passage mentionnées dans un lot de documents scannés complexes."
        },
        {
          title: "Dossiers de Succession",
          description: "Croisez les testaments, donations et livrets de famille pour vérifier la cohérence des droits des héritiers."
        }
      ]}
      faq={[
        { q: "L'hébergement est-il compatible avec les exigences du notariat ?", a: "Nos serveurs sont hébergés chez OVH en France, assurant une sécurité et une confidentialité (compatible hébergeurs de données de santé et SecNumCloud) conformes aux exigences professionnelles de la profession." },
        { q: "L'IA sait-elle lire les actes authentiques anciens et numérisés ?", a: "Notre OCR déchiffre très bien les documents dactylographiés anciens numérisés. Concernant les actes 100% manuscrits très anciens, la lecture automatique reste encore expérimentale." },
        { q: "Comment Raguia cite-t-il ses sources ?", a: "Pour chaque réponse, l'IA indique précisément le nom du document source et la page exacte d'où est tirée l'information, ce qui vous permet de vérifier la validité de la réponse." },
      ]}
    />
  );
}

export function SecteurComptablesPage() {
  return (
    <SecteurTemplate
      metier="Experts-Comptables"
      slug="comptables"
      title="IA pour Experts-Comptables et DAF | Raguia"
      description="Automatisez la recherche dans vos bilans, liasses fiscales et justificatifs. Raguia est l'assistant documentaire des professionnels du chiffre."
      heroTitle="Libérez votre cabinet de la recherche documentaire"
      heroSubtitle="Bilans, liasses fiscales, justificatifs : interrogez vos données comptables et fiscales avec précision et sécurité."
      painPoints={[
        "Recherche chronophage dans des dossiers clients (justificatifs, factures, AG).",
        "Veille fiscale et sociale éparpillée dans de multiples bulletins officiels.",
        "Difficulté à faire des synthèses multi-documents lors de la période fiscale."
      ]}
      solutions={[
        "Raguia lit les tableaux Excel, les factures scannées et les liasses PDF.",
        "Retrouvez instantanément le traitement fiscal appliqué l'année précédente pour un client.",
        "Portails séparés : aucune chance que les données du Client A fuitent chez le Client B."
      ]}
      useCases={[
        {
          title: "Analyse de Bilans",
          description: "Demandez : 'Quelles étaient les provisions pour risques l'année dernière ?'. Raguia extrait la donnée exacte de l'annexe comptable."
        },
        {
          title: "Dossier Permanent",
          description: "Interrogez l'historique des statuts, baux et AG d'un client sans avoir à relire tout son dossier permanent."
        },
        {
          title: "Veille Fiscale",
          description: "Chargez vos revues fiduciaires ou circulaires, et posez vos questions pointues pour sécuriser vos conseils."
        }
      ]}
      faq={[
        { q: "L'IA comprend-elle les tableaux chiffrés dans les bilans ?", a: "Oui, notre pipeline d'analyse extrait les tableaux des PDF (comme les liasses fiscales et annexes) de manière structurée pour que l'IA puisse répondre aux questions sur des données chiffrées précises." },
        { q: "Pouvons-nous séparer strictement les dossiers de nos différents clients ?", a: "Absolument. La fonctionnalité de multi-tenant ou d'isolation par client permet de créer un espace cloisonné pour chaque dossier client, sans aucun risque de porosité." },
        { q: "Est-il possible d'utiliser Raguia pour notre veille fiscale ?", a: "Oui, en téléchargeant vos revues fiduciaires, Mémentos ou circulaires, vous créez une base de connaissances fiscale interrogable instantanément pour fiabiliser vos conseils." },
      ]}
    />
  );
}
